import { forwardRef, useCallback, useEffect, useState } from 'react';
import { Dialog as MuiDialog, DialogContentText, useMediaQuery } from '@mui/material';
import { ModalTitle } from './ModalTitle';
import DialogContent from '@mui/material/DialogContent';
import { modalDefaultStyle } from './modalDefaultStyle';
import { mergeSxStyle } from '../common/mergeSxStyle';
import { useInterstateTheme } from '../InterstateThemeProvider';
import { Typography } from '../Typography';
import { idBuilder } from '../internal';
import { clsx } from 'clsx';
import { largeOnXSStyle } from './largeOnXSStyle';
import debounce from 'lodash.debounce';
import { FloatingFooter } from './FloatingFooter';
import { FixedFooter } from './FixedFooter';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const resolveContent = function (content, id, dataTestId) {
  let customIdentifiers = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
  if (typeof content === 'string') {
    return /*#__PURE__*/_jsx(DialogContentText, {
      className: 'ids-modal-content-text',
      id: customIdentifiers['contentText']?.id || idBuilder('ids-modal-content-text', id),
      "data-testid": customIdentifiers['contentText']?.dataTestId || idBuilder('ids-modal-content-text', dataTestId),
      children: content
    });
  }
  return content;
};
/** Overlays that show details/functionality in context  */
export const Modal = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    id,
    className,
    header,
    footer,
    children,
    onHide,
    show,
    size,
    sx,
    padding = true,
    customIdentifiers = {},
    disableEnforceFocus = false,
    ...rest
  } = props;
  const [effectiveSize, setEffectiveSize] = useState(size);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [dimensions, setDimensions] = useState({
    height: window.innerHeight,
    width: window.innerWidth
  });
  const theme = useInterstateTheme();
  const debouncedSetDimensions = debounce(setDimensions, 500);
  useEffect(() => {
    const handleResize = () => {
      debouncedSetDimensions({
        height: window.innerHeight,
        width: window.innerWidth
      });
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      debouncedSetDimensions.cancel();
    };
  });
  const containerRef = useCallback(node => {
    if (node !== null) {
      setHasOverflow(node.scrollHeight > node.clientHeight);
    }
  }, [dimensions]);
  const useLargeXS = useMediaQuery('(max-width:479px)');
  useEffect(() => {
    useLargeXS && size === 'large' ? setEffectiveSize('largeOnXS') : setEffectiveSize(size);
  }, [size, useLargeXS]);
  const dialogStyle = effectiveSize === 'largeOnXS' ? mergeSxStyle(largeOnXSStyle(theme, 'largeOnXS', hasOverflow, padding, footer?.xsLayout), sx) : mergeSxStyle(modalDefaultStyle(theme, effectiveSize, padding), sx);
  const modalTitleId = idBuilder('ids-modal-title', id);
  // Only display one option button
  const effectiveOptions = size === 'small' && footer?.options && Array.isArray(footer?.options) && footer.options.length > 1 ? footer?.options[0] : footer?.options;
  return /*#__PURE__*/_jsxs(MuiDialog, {
    open: show,
    onClose: onHide,
    "aria-labelledby": modalTitleId,
    fullScreen: effectiveSize === 'fullScreen' || effectiveSize === 'largeOnXS',
    ref: ref,
    sx: dialogStyle,
    className: clsx([className, 'ids-modal-root']),
    id: customIdentifiers['root']?.id || idBuilder('ids-modal-root', id),
    "data-testid": customIdentifiers['root']?.dataTestId || idBuilder('ids-modal-root', rest['data-testid']),
    disableEnforceFocus: disableEnforceFocus,
    children: [/*#__PURE__*/_jsx(ModalTitle, {
      onClose: onHide,
      id: id,
      displayCloseButton: size === 'large',
      size: effectiveSize,
      "data-testid": rest['data-testid'],
      customIdentifiers: customIdentifiers,
      children: header && /*#__PURE__*/_jsx(Typography, {
        variant: "h4",
        sx: {
          margin: '0'
        },
        id: customIdentifiers['titleText']?.id,
        "data-testid": customIdentifiers['titleText']?.dataTestId,
        children: header
      })
    }), /*#__PURE__*/_jsxs(DialogContent, {
      className: 'ids-modal-content',
      id: customIdentifiers['content']?.id || idBuilder('ids-modal-content', id),
      "data-testid": customIdentifiers['content']?.dataTestId || idBuilder('ids-modal-content', rest['data-testid']),
      ref: containerRef,
      children: [resolveContent(children, id, rest['data-testid'], customIdentifiers), /*#__PURE__*/_jsx(FloatingFooter, {
        size: effectiveSize,
        options: effectiveOptions,
        "data-testid": rest['data-testid'],
        id: id,
        footer: footer,
        hasOverflow: hasOverflow,
        customIdentifiers: customIdentifiers
      })]
    }), /*#__PURE__*/_jsx(FixedFooter, {
      size: effectiveSize,
      options: effectiveOptions,
      "data-testid": rest['data-testid'],
      id: id,
      footer: footer,
      hasOverflow: hasOverflow,
      customIdentifiers: customIdentifiers
    })]
  });
});
Modal.displayName = 'Modal';